import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { selectPortfolioPropertyList } from '@platform/portfolio/selectors/portfolio-property.selector';
import { selectPortfolioSmartSearches } from '@platform/portfolio/selectors/portfolio-smart-search.selector';
import { selectIsLoadingPortfolio } from '@platform/portfolio/selectors/portfolio.selectors';
import {
  AddMortgageSuggestionDialogComponent,
  AddMortgageSuggestionDialogData,
} from '@platform/property/components/add-mortgage-suggestion-dialog/add-mortgage-suggestion-dialog.component';
import { AddSmartSearchDialogComponent } from '@platform/smart-search/pages/add-smart-search-dialog/add-smart-search-dialog.component';
import { addSmartSearchSuccess } from '@shared/store/smart-searches/smart-searches.actions';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

export const ADD_NEW_PROPERTY_PAGE_ID = 'ADD_NEW_PROPERTY';

interface PortfolioCarouselState {
  pagePropertyIndex: number;
}

const defaultState: PortfolioCarouselState = {
  pagePropertyIndex: 0,
};

@Injectable()
export class PortfolioCarouselComponentStore extends ComponentStore<PortfolioCarouselState> {
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly globalStore = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly location = inject(Location);

  constructor() {
    super(defaultState);
  }

  // HELPERS -----------------------------------------------------------------------------------------------------------
  readonly redirectToPropertyTrigger$ = new BehaviorSubject<void>(undefined);

  readonly setRouteParamItemId = (openedOnId: string, propertyIndex: number): void => {
    const [url, _params] = this.router.url.split('?id=');
    this.location.replaceState(`${url}?id=${openedOnId}`);
    this.setPagePropertyIndex(propertyIndex);
  };

  // SELECTORS ---------------------------------------------------------------------------------------------------------
  readonly isLoadingPortfolio$ = this.globalStore.select(selectIsLoadingPortfolio);
  readonly portfolioPropertyItems$ = this.globalStore.select(selectPortfolioPropertyList);
  readonly smartSearchItems$ = this.globalStore.select(selectPortfolioSmartSearches);
  readonly pagePropertyIndex$ = this.select((state) => state.pagePropertyIndex);

  // EFFECTS -----------------------------------------------------------------------------------------------------------
  readonly redirectToProperty = this.effect((propertyId$: Observable<{ propertyId: string }>) =>
    propertyId$.pipe(
      switchMap(({ propertyId }) =>
        this.router.navigate(['/', paths.PLATFORM, paths.platform.PROPERTIES], {
          queryParams: { id: propertyId },
        }),
      ),
      map(() => {
        this.redirectToPropertyTrigger$.next();
      }),
    ),
  );

  readonly openAddSmartSearchDialog = this.effect((openAddSmartSearchDialog$: Observable<void>) =>
    openAddSmartSearchDialog$.pipe(
      tap(() => {
        const smartSearchFormDialogRef = this.dialogService.open(AddSmartSearchDialogComponent, {
          closable: false,
          closeOnEscape: true,
          transitionOptions: '0ms',
        });
        smartSearchFormDialogRef.onClose.pipe(take(1)).subscribe((smartSearch) => {
          if (smartSearch) {
            void this.router.navigate([paths.PLATFORM, paths.platform.SMART_SEARCH, smartSearch.id]);
            this.globalStore.dispatch(addSmartSearchSuccess({ smartSearch }));
          }
        });
      }),
    ),
  );

  readonly openAddMortgageSuggestionDialog = this.effect((openAddMortgageSuggestionDialog$) =>
    openAddMortgageSuggestionDialog$.pipe(
      withLatestFrom(this.portfolioPropertyItems$),
      map(([_, portfolio]) => {
        const id = this.activatedRoute.snapshot.queryParams.id;
        const property = portfolio.find((p) => p.property?.id == id)?.property;

        if (property === null) return;

        const data: AddMortgageSuggestionDialogData = { propertyId: id, propertyName: property.name };
        this.dialogService.open(AddMortgageSuggestionDialogComponent, {
          closable: false,
          closeOnEscape: true,
          transitionOptions: '0ms',
          data,
        });
      }),
    ),
  );

  readonly redirectToRouteId = this.effect(() =>
    combineLatest([this.smartSearchItems$, this.portfolioPropertyItems$, this.isLoadingPortfolio$, this.redirectToPropertyTrigger$]).pipe(
      filter(([_s, _p, isLoadingPortfolio]) => !isLoadingPortfolio),
      switchMap(([smartSearches, properties]) => {
        const id = this.activatedRoute.snapshot.queryParams.id;

        if (id === ADD_NEW_PROPERTY_PAGE_ID) {
          return of(smartSearches.length + properties.length);
        }

        const onProperty = properties.findIndex((p) => p.property?.id == id);
        if (onProperty != -1) return of(onProperty);

        const findSmartSearch = smartSearches.findIndex((s) => s.id == id);
        if (findSmartSearch != -1) return of(properties.length + findSmartSearch);

        return of(0);
      }),
      map((pagePropertyIndex: number) => {
        requestAnimationFrame(() => {
          this.setPagePropertyIndex(pagePropertyIndex);

          const showMortgageSuggestion = this.activatedRoute.snapshot.queryParams.showMortgageSuggestion;
          if (showMortgageSuggestion === 'true') {
            this.openAddMortgageSuggestionDialog();
          }
        });
      }),
    ),
  );

  // UPDATERS ----------------------------------------------------------------------------------------------------------
  private readonly setPagePropertyIndex = this.updater<number>((state, pagePropertyIndex) => ({
    ...state,
    pagePropertyIndex: pagePropertyIndex,
  }));
}
